import { useMemo } from "react"

import countries from "../../../../../public/countries.json"

import { FIELD_CLASS, FieldError, TextField } from "@/components/tuw/auth/fields"
import { Combobox } from "@/components/ui/combobox"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

// The existing country list (public/countries.json), with the regions of the
// countries that have them. A country with regions requires one (the server's
// OpenHouse::PaymentDesk reads the same file).
const COUNTRY_OPTIONS = countries
  .map((country) => ({ value: country.name, label: country.name }))
  .sort((a, b) => a.label.localeCompare(b.label))
export const REGIONS = Object.fromEntries(
  countries
    .filter((country) => country.provinces.length > 0)
    .map((country) => [country.name, country.provinces.map((province) => province.province)])
)

export const PICKER_CLASS = cn(FIELD_CLASS, "h-auto py-3 font-normal disabled:opacity-100 disabled:bg-secondary disabled:text-muted-foreground")
export const LABEL_CLASS = "text-[12.5px] font-bold"

// A saved value the list does not carry still has to display (Settings does
// the same for the sign-up default time zone).
export function withCurrent(options, value) {
  if (!value || options.some((option) => option.value === value)) return options
  return [{ value, label: value }, ...options]
}

// The country picker and, beside it, the region: a required picker for a
// country with regions, free text otherwise. Both carry their value in a
// `customer[...]` hidden field. Changing the country clears the region.
// `copy` holds each screen's own words.
export default function CountryRegionFields({
  country,
  region,
  onCountryChange,
  onRegionChange,
  saved = {},
  locked = {},
  regionError,
  countryError,
  copy,
}) {
  const regions = REGIONS[country] ?? null
  const countryOptions = useMemo(() => withCurrent(COUNTRY_OPTIONS, saved.country), [saved.country])
  const regionOptions = useMemo(() => (regions ? regions.map((name) => ({ value: name, label: name })) : []), [regions])

  return (
    <>
      <div className="flex flex-col gap-[7px]">
        <Label htmlFor="customer_country" className={LABEL_CLASS}>
          {copy.countryLabel}
        </Label>
        <input type="hidden" name="customer[country]" value={country} />
        <Combobox
          id="customer_country"
          options={countryOptions}
          value={country}
          onValueChange={onCountryChange}
          placeholder={copy.countryPlaceholder}
          searchPlaceholder={copy.countrySearch}
          emptyText={copy.countryEmpty}
          disabled={locked.country}
          className={cn(PICKER_CLASS, countryError && "border-primary")}
          data-testid="country-combobox"
        />
        {countryError && <FieldError>{countryError}</FieldError>}
      </div>
      {regions ? (
        <div className="flex flex-col gap-[7px]">
          <Label htmlFor="customer_region" className={LABEL_CLASS}>
            {copy.regionLabel}
          </Label>
          <input type="hidden" name="customer[region]" value={region} />
          <Combobox
            id="customer_region"
            options={withCurrent(regionOptions, saved.region)}
            value={region}
            onValueChange={onRegionChange}
            placeholder={copy.regionPlaceholder}
            searchPlaceholder={copy.regionSearch}
            emptyText={copy.regionEmpty}
            disabled={locked.region}
            className={cn(PICKER_CLASS, regionError && "border-primary")}
            data-testid="region-combobox"
          />
          {regionError && <FieldError>{regionError}</FieldError>}
        </div>
      ) : (
        <TextField
          id="customer_region"
          name="customer[region]"
          label={copy.regionOptionalLabel}
          placeholder={copy.regionTextPlaceholder}
          autoComplete="address-level1"
          value={region}
          onChange={(event) => onRegionChange(event.target.value)}
          disabled={locked.region}
        />
      )}
    </>
  )
}
